/**
 * HomeCanal — Visão Geral do Canal
 *
 * KPIs: Clientes Ativos, Clientes em Alerta, Adesão Média
 * Insights: Top 3 insights automáticos
 * Dados: 100% reais via homeOverview.channel.overview
 */
import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Users, ShieldAlert, AlertCircle, ClipboardCheck } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { GaugeChart, InsightsCard, IndicatorManualModal } from "@/components/home";
import IndicadorTooltip from "@/components/IndicadorTooltip";

export default function HomeCanal() {
  usePageTitle("Visão Geral — Canal");

  const { data, isLoading, error } = trpc.homeOverview.channel.overview.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const { data: advInsights } = trpc.dashboardsAdv.insights.byProfile.useQuery(
    { profile: "channel" },
    { refetchOnWindowFocus: false }
  );

  if (isLoading) {
    return (
      <SalviaLayout title="Visão Geral — Canal">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6"><Skeleton className="h-4 w-24 mb-3" /><Skeleton className="h-10 w-20" /></Card>
            ))}
          </div>
          <Card className="p-6"><Skeleton className="h-[200px]" /></Card>
        </div>
      </SalviaLayout>
    );
  }

  if (error) {
    return (
      <SalviaLayout title="Visão Geral — Canal">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <h2 className="text-lg font-semibold mb-2">Erro ao carregar dashboard</h2>
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  if (!data) return null;

  const { kpis, insights } = data;
  const alertas = kpis.clientesEmAlerta.alertas || [];

  return (
    <SalviaLayout title="Visão Geral — Canal">
      <div className="space-y-6">
        {/* ─── 3 KPIs ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Clientes Ativos */}
          <Card className="p-5 border-l-4" style={{ borderLeftColor: "#002F59" }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Clientes Ativos</p>
                  <IndicadorTooltip indicadorKey="clientesAtivos" side="top" size={12} />
                </div>
                <p className="text-3xl font-bold text-[#002F59] mt-1">{kpis.clientesAtivos.value}</p>
              </div>
              <div className="p-3 rounded-full bg-[#002F59]/10">
                <Users size={24} className="text-[#002F59]" />
              </div>
            </div>
          </Card>

          {/* Clientes em Alerta */}
          <Card className="p-5 border-l-4" style={{ borderLeftColor: kpis.clientesEmAlerta.value > 0 ? "#DC2626" : "#16A34A" }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Clientes em Alerta</p>
                  <IndicadorTooltip indicadorKey="clientesAlerta" side="top" size={12} />
                </div>
                <p className={`text-3xl font-bold mt-1 ${kpis.clientesEmAlerta.value > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>
                  {kpis.clientesEmAlerta.value}
                </p>
              </div>
              <div className={`p-3 rounded-full ${kpis.clientesEmAlerta.value > 0 ? "bg-red-50" : "bg-green-50"}`}>
                <ShieldAlert size={24} className={kpis.clientesEmAlerta.value > 0 ? "text-[#DC2626]" : "text-[#16A34A]"} />
              </div>
            </div>
          </Card>

          {/* Adesão Média (Gauge) */}
          <Card className="p-5">
            <div className="flex items-center gap-1 mb-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Adesão Média</p>
              <IndicadorTooltip indicadorKey="adesaoMedia" side="top" size={12} />
            </div>
            <div className="flex items-center justify-center">
              <GaugeChart
                value={kpis.adesaoMedia.value}
                label="Adesão Média"
                subtitle={`${kpis.adesaoMedia.value}%`}
                size={130}
                ranges={[
                  { min: 0, max: 30, color: "#DC2626", label: "Baixa" },
                  { min: 30, max: 60, color: "#D97706", label: "Moderada" },
                  { min: 60, max: 100, color: "#16A34A", label: "Boa" },
                ]}
              />
            </div>
          </Card>
        </div>

        {/* ─── Clientes em Alerta (detalhes) ─── */}
        {alertas.length > 0 && (
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle size={18} className="text-[#DC2626]" />
              <h3 className="text-sm font-semibold text-[#002F59]">Clientes que Precisam de Atenção</h3>
            </div>
            <div className="space-y-2">
              {alertas.map((a: any, i: number) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-red-50/50 rounded-lg border border-red-100">
                  <ShieldAlert size={16} className="text-[#DC2626] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{a.companyName}</p>
                    <p className="text-xs text-muted-foreground">{a.motivo}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* ─── Insights Automáticos ─── */}
        {(() => {
          const allInsights = [
            ...insights,
            ...(advInsights?.insights || []).filter((ai: any) => !insights.some((i: any) => i.titulo === ai.titulo)),
          ];
          return allInsights.length > 0 ? (
            <Card className="p-5">
              <InsightsCard insights={allInsights as any[]} title="Insights Operacionais" />
            </Card>
          ) : null;
        })()}

        {/* ─── Empty state ─── */}
        {alertas.length === 0 && insights.length === 0 && kpis.clientesAtivos.value === 0 && (
          <Card className="p-8 text-center border-dashed">
            <ClipboardCheck className="mx-auto mb-4 text-muted-foreground opacity-40" size={48} />
            <h2 className="text-lg font-semibold mb-2">Nenhum dado disponível ainda</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Os indicadores do canal aparecerão aqui após a importação de clientes e criação de campanhas.
              Importe dados de clientes para começar o processo NR-1.
            </p>
          </Card>
        )}
      </div>
      <IndicatorManualModal profile="channel" />
    </SalviaLayout>
  );
}
